import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle, Crosshair, KeyRound, Link2, MapPin, Network as NetworkIcon, RefreshCw, Settings,
  User, Wrench,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { UpperInput } from "@/components/ui/text-input";
import {
  contratoCreateSchema,
  cpeVendorEnum, CPE_VENDOR_LABEL,
  polarizacionEnum,
  type ContratoCreateInput, type ContratoCreateResponse,
} from "./schemas";
import { useCreateContrato } from "./api";
import { useListClientes, useCliente } from "@/features/clientes/api";
import { useConfigByGrupo } from "@/features/config/api";
import { useListPlanes } from "@/features/planes/api";
import { useListRouters } from "@/features/routers/api";
import { useListRedes, useNextIpRed } from "@/features/redes/api";
import { useListSitios } from "@/features/sitios/api";
import {
  useOnusHuerfanas, usePlantillasOlt, usePerfilesOlt,
  useEquipoAcs, useEquiposAlmacen, useOltsByteolt,
  type OnuHuerfana,
} from "@/features/integraciones/api";
import { LabelCaptureOnu, fmtMac } from "@/components/LabelCaptureOnu";
import { genWifiPass } from "@/lib/wifiCard";
import { PasswordRevealDialog } from "./PasswordRevealDialog";
import { EmpresaChip } from "@/features/empresas/EmpresaChip";
import type { ApiError } from "@/lib/api";
import { cn, formatMXN } from "@/lib/utils";

const DEFAULTS: ContratoCreateInput = {
  cliente_id: "",
  plan_id: "",
  router_id: "",
  red_id: "",
  ip_asignada: "",
  pppoe_user: "",
  pppoe_password: "",
  fecha_corte: 1,
  precio_override: "",
  descuento_pct: 0,
  dias_gracia_override: "",
  exento_facturacion: false,
  exento_monitoreo: false,
  // F92 — Equipo
  equipo_modalidad: "comodato",
  equipo_descripcion: "",
  equipo_costo_instalacion: 0,
  // rc145 — ubicación GPS de la instalación desde el alta
  latitud: "",
  longitud: "",
  ubicacion_referencia: "",
  notas: "",
  notas_internas: "",
  tags: [],
  wireless_detalle: {},
  fiber_detalle: {},
};

/** rc145 — pestaña que "posee" cada campo del formulario: con esto la pestaña
 *  con error se marca con un contador y el submit salta a ella en vez de
 *  fallar en silencio desde otra pestaña. */
type AltaTab = "cliente-plan" | "red" | "detalle" | "pppoe" | "config";
const TAB_LABEL: Record<AltaTab, string> = {
  "cliente-plan": "Cliente & Plan", red: "Router & Red", detalle: "Detalle",
  pppoe: "PPPoE", config: "Facturación",
};
function tabOfField(path: string): AltaTab {
  if (path === "cliente_id" || path === "plan_id") return "cliente-plan";
  if (path === "router_id" || path === "red_id" || path === "ip_asignada") return "red";
  if (path === "pppoe_user" || path === "pppoe_password") return "pppoe";
  if (
    path.startsWith("wireless_detalle") || path.startsWith("fiber_detalle")
    || path.startsWith("byteolt") || path.startsWith("byteacs")
    || path === "latitud" || path === "longitud" || path === "ubicacion_referencia"
  ) return "detalle";
  return "config";
}
const FIELD_LABEL: Record<string, string> = {
  cliente_id: "cliente", plan_id: "plan", router_id: "router", red_id: "red",
  ip_asignada: "IP asignada", pppoe_user: "usuario PPPoE", pppoe_password: "password PPPoE",
  fecha_corte: "día de corte", precio_override: "precio", dias_gracia_override: "días de gracia",
  descuento_pct: "descuento", latitud: "latitud", longitud: "longitud",
  ubicacion_referencia: "referencia", equipo_costo_instalacion: "costo de instalación",
};
function labelOfField(path: string): string {
  if (FIELD_LABEL[path]) return FIELD_LABEL[path];
  const last = path.split(".").pop() ?? path;
  return last.replace(/_/g, " ");
}

/** rc145 — saca lat/lng de un enlace de Google Maps / Apple Maps / "lat, lng". */
function parseCoords(text: string): { lat: number; lng: number } | null {
  const t = text.trim();
  const pats = [
    /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/,            // google: .../@20.38,-99.99,17z
    /[?&](?:q|query|ll|destination)=(-?\d{1,2}\.\d+)[, ]+(-?\d{1,3}\.\d+)/, // ?q=lat,lng
    /!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/,          // google: !3dlat!4dlng
    /^\s*(-?\d{1,2}\.\d+)\s*[, ]\s*(-?\d{1,3}\.\d+)\s*$/,  // "20.38, -99.99"
  ];
  for (const re of pats) {
    const m = re.exec(t);
    if (m) {
      const lat = Number(m[1]); const lng = Number(m[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }
  }
  return null;
}

/** rc145 — próximo día de corte ≥ hoy (cap 28) y fecha de emisión estimada. */
function proximoCorte(dia: number, hoy = new Date()): Date {
  const d = Math.min(Math.max(dia, 1), 28);
  const c = new Date(hoy.getFullYear(), hoy.getMonth(), d);
  if (c < new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())) c.setMonth(c.getMonth() + 1);
  return c;
}
function fmtFecha(d: Date): string {
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return Object.keys(out).length > 0 ? (out as T) : (undefined as T);
  }
  return v;
}

/** Aplana errores anidados de RHF a lista de mensajes "campo: mensaje". */
function flattenErrors(
  errors: Record<string, unknown>,
  prefix = ""
): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(errors)) {
    if (!v) continue;
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "object" && v !== null) {
      if ("message" in v && typeof (v as { message?: unknown }).message === "string") {
        out.push(`${key}: ${(v as { message: string }).message}`);
      } else {
        out.push(...flattenErrors(v as Record<string, unknown>, key));
      }
    }
  }
  return out;
}

// Nota: el PPPoE user/pass pre-llenado usa la MAC TAL CUAL el admin la escribe
// (con ":" y mayúsculas si así la tecleó). Solo trimmea espacios al inicio/fin.

/** rc109 — forma LARGA/hex de la serie de la ONU (la de la etiqueta, la que
 *  siempre se teclea en ByteISP): "HWTCe677d3b7" → "48575443E677D3B7".
 *  ByteOLT reporta la legible (4 letras + 8 hex); las 4 letras del vendor se
 *  convierten a su hex ASCII. Si ya viene en hex de 16, solo se sube a mayús. */
function serialLargo(s: string): string {
  // rc120 — la CLI de la OLT puede imprimir la serie CON separador
  // (TP-Link autofind: "HWTC-E677D3B7") — se limpia antes de convertir,
  // si no el picker mostraba la forma cruda y el PPPoE no se pre-llenaba.
  const t = s.trim().replace(/[^0-9A-Za-z]/g, "");
  if (/^[0-9a-fA-F]{16}$/.test(t)) return t.toUpperCase();
  const m = /^([A-Za-z]{4})([0-9a-fA-F]{8})$/.exec(t);
  if (!m) return t || s.trim();
  const pfx = [...m[1]]
    .map((c) => c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0"))
    .join("");
  return pfx + m[2].toUpperCase();
}

/** rc145 — valores iniciales (p.ej. al convertir un prospecto: GPS + referencia). */
export type ContratoFormInitial = {
  tipoServicio?: "wireless" | "fiber";
  latitud?: number | null;
  longitud?: number | null;
  ubicacion_referencia?: string | null;
  notas_internas?: string | null;
};

export function ContratoFormDialog({
  open, onOpenChange, clientePreselectId, initial, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientePreselectId?: string;
  initial?: ContratoFormInitial;
  /** rc145 — se llama al confirmar las credenciales (antes de navegar al contrato) */
  onCreated?: (contratoId: string) => void;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("cliente-plan");
  const [error, setError] = useState<string | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<ContratoCreateResponse | null>(null);
  // rc124 — modal de confirmación PRE-creación: payload listo en espera +
  // monto de la factura de instalación (editable; 0 = sin factura)
  const [confirmData, setConfirmData] = useState<ContratoCreateInput | null>(null);
  const [instMonto, setInstMonto] = useState("0");
  // Checkboxes locales para habilitar cada campo override (dev60 UX fix).
  // Default OFF en creación — el caso raro es override, no el default.
  const [useOverridePrecio, setUseOverridePrecio] = useState(false);
  const [useOverrideGracia, setUseOverrideGracia] = useState(false);

  // F108 — selector tipo servicio para filtrar planes.
  // rc109 — se quitó "Todos" (pedido Alex): siempre se elige wireless o fibra.
  const [tipoFiltro, setTipoFiltro] = useState<"wireless" | "fiber">("wireless");

  // rc108 — alta de fibra vía ByteOLT: ONU huérfana elegida + plantilla/perfil
  const [onuSerialSel, setOnuSerialSel] = useState("");
  const [templateSel, setTemplateSel] = useState("");
  const [perfilSel, setPerfilSel] = useState("");
  // rc111 — apartado Módem · ACS (TR-069): serial editable/escaneable +
  // consulta en vivo + lista de almacén
  const [acsSerial, setAcsSerial] = useState("");
  const [acsSerialQuery, setAcsSerialQuery] = useState("");
  // rc121 — clave WiFi opcional asignada desde el alta (ByteACS la aplica en
  // el primer Inform y queda imprimible en la tarjeta QR)
  const [wifiPass, setWifiPass] = useState("");
  // rc122 — SSID personalizado (cliente que conserva su red anterior)
  const [ssidCustom, setSsidCustom] = useState("");
  const [showAlmacen, setShowAlmacen] = useState(false);
  const [showCaptura, setShowCaptura] = useState(false);
  const acsSerialDirty = useRef(false);
  // rc112 — PRE-registro: la OLT aún no ve la ONU (serial + OLT destino)
  const [preMode, setPreMode] = useState(false);
  const [preSerial, setPreSerial] = useState("");
  const [preOltId, setPreOltId] = useState("");
  // rc118 — MATCH: ya aprovisionada Y LIGADA (cliente importado). Solo se
  // re-liga en ByteOLT (cero CLI) y NO se manda nada a ByteACS.
  const [matchMode, setMatchMode] = useState(false);
  const [matchSerial, setMatchSerial] = useState("");

  // Flags para saber si el user ya editó un campo manualmente (no sobreescribir)
  const pppoeUserDirty = useRef(false);
  const pppoePassDirty = useRef(false);
  const ipAsignadaDirty = useRef(false);
  const cpeMgmtDirty = useRef(false);
  const onuMgmtDirty = useRef(false);

  const {
    register, handleSubmit, control, reset, watch, setValue,
    formState: { errors, isSubmitted },
  } = useForm<ContratoCreateInput>({
    resolver: zodResolver(contratoCreateSchema),
    defaultValues: DEFAULTS,
  });

  // rc145 — errores agrupados por pestaña (contador en la pestaña + banner).
  // Sin useMemo a propósito: `errors` es un proxy que RHF muta en sitio, así
  // que memoizar por identidad dejaba el contador viejo tras corregir el campo.
  const errorsByTab: Record<AltaTab, string[]> = {
    "cliente-plan": [], red: [], detalle: [], pppoe: [], config: [],
  };
  for (const msg of flattenErrors(errors as Record<string, unknown>)) {
    const path = msg.split(":")[0];
    errorsByTab[tabOfField(path)].push(labelOfField(path));
  }
  const tabsConError = (Object.keys(errorsByTab) as AltaTab[]).filter((t) => errorsByTab[t].length > 0);
  const onInvalid = (errs: Record<string, unknown>) => {
    const first = flattenErrors(errs)[0];
    if (first) setTab(tabOfField(first.split(":")[0]));
    setError(null);
  };
  function TabBadge({ t }: { t: AltaTab }) {
    const n = errorsByTab[t].length;
    if (!n || !isSubmitted) return null;
    return (
      <span
        aria-label={`${n} campo(s) con error`}
        className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground"
      >
        {n}
      </span>
    );
  }

  // Sincroniza checkbox → valor del form
  function togglePrecioOverride(on: boolean) {
    setUseOverridePrecio(on);
    if (!on) setValue("precio_override", "", { shouldValidate: false });
  }
  function toggleGraciaOverride(on: boolean) {
    setUseOverrideGracia(on);
    if (!on) setValue("dias_gracia_override", "", { shouldValidate: false });
  }

  // Watch
  const selectedClienteId = watch("cliente_id");
  const selectedPlanId = watch("plan_id");
  const selectedRouterId = watch("router_id");
  const selectedRedId = watch("red_id");
  const ipAsignada = watch("ip_asignada");
  const cpeMac = watch("wireless_detalle.cpe_mac");
  const onuMac = watch("fiber_detalle.onu_mac");

  // Cargar catálogos
  const { data: clientesData } = useListClientes({ page: 1, size: 200 });
  const { data: planesData } = useListPlanes({ page: 1, size: 200 });
  const { data: routersData } = useListRouters({ page: 1, size: 200 });
  const { data: redesData } = useListRedes({ page: 1, size: 200 });
  const { data: sitiosData } = useListSitios({ page: 1, size: 200 });

  // Plan seleccionado
  const selectedPlan = useMemo(
    () => planesData?.items.find((p) => p.id === selectedPlanId),
    [planesData, selectedPlanId]
  );
  const tipoServicio = selectedPlan?.tipo_servicio;

  // rc108 — Integración ByteOLT: ONUs por autorizar en vivo. `enabled` dice
  // si la fibra está disponible (sin integración NO hay servicio de fibra).
  const {
    data: byteoltData, isFetching: onusLoading, refetch: refetchOnus,
  } = useOnusHuerfanas(open);
  const byteoltEnabled = byteoltData?.enabled; // undefined = cargando
  const onusHuerfanas = byteoltData?.onus ?? [];
  // rc110 — ya aprovisionadas SIN cliente (el técnico las autorizó desde
  // ByteOLT antes del alta): elegir una de estas solo LIGA, sin re-aprovisionar
  const onusSinCliente = byteoltData?.sin_cliente ?? [];
  const onuSel =
    onusHuerfanas.find((o) => o.serial === onuSerialSel) ??
    onusSinCliente.find((o) => o.serial === onuSerialSel);
  const onuYaAprov = onusSinCliente.some((o) => o.serial === onuSerialSel);
  // rc112 — las plantillas/perfiles se cargan para la OLT del pre-registro o
  // para la de la ONU elegida (nunca para una ya-aprovisionada: solo se liga)
  const plantillaOltId = preMode
    ? (preOltId || null)
    : !onuYaAprov ? onuSel?.olt_id ?? null : null;
  const { data: plantillas } = usePlantillasOlt(plantillaOltId);
  const { data: perfiles } = usePerfilesOlt(plantillaOltId);
  const { data: oltsData } = useOltsByteolt(open && byteoltEnabled === true && preMode);
  const preOlt = (oltsData ?? []).find((o) => o.id === preOltId);

  // rc111 — Módem · ACS: consulta en vivo (debounced) + lista de almacén
  const byteacsEnabled = byteoltData?.byteacs_enabled === true;
  const acsActivo = byteacsEnabled && tipoServicio === "fiber";
  const { data: equipoAcs, isFetching: equipoAcsLoading } = useEquipoAcs(
    acsSerialQuery, open && acsActivo,
  );
  const { data: almacenData, isFetching: almacenLoading } = useEquiposAlmacen(
    open && acsActivo && showAlmacen,
  );

  // Vendor de la ONU (enum ByteISP) inferido del vendor que reporta ByteOLT
  const onuVendorGuess = useMemo(() => {
    const v = (onuSel?.vendor ?? "").toLowerCase();
    if (!v) return "";
    if (v.includes("huaw") || v.includes("hwtc")) return "huawei";
    if (v.includes("tpl")) return "tp_link";
    if (v.includes("vsol")) return "vsol";
    if (v.includes("zte")) return "zte";
    if (v.includes("nokia")) return "nokia";
    return "otro";
  }, [onuSel?.vendor]);

  // Redes filtradas por router
  const redesFiltradas = useMemo(() => {
    if (!redesData) return [];
    if (!selectedRouterId) return [];
    return redesData.items.filter(
      (r) => r.router_id === selectedRouterId && r.activo && !r.uso_interno
    );
  }, [redesData, selectedRouterId]);

  // Preview de próxima IP de la red seleccionada
  const { data: nextIpData } = useNextIpRed(selectedRedId || null);

  // ── Auto-fills ──

  // rc108 — ONU elegida → PPPoE user/pass = serie (si no están dirty), y
  // limpia plantilla/perfil al cambiar de ONU (pueden ser de otra OLT).
  // rc111 — también sincroniza el serial del apartado Módem · ACS.
  useEffect(() => {
    setTemplateSel("");
    setPerfilSel("");
    if (tipoServicio !== "fiber" || !onuSerialSel) return;
    // rc109 — SIEMPRE la forma larga/hex (la de la etiqueta), no la legible
    const largo = serialLargo(onuSerialSel);
    if (!pppoeUserDirty.current) setValue("pppoe_user", largo, { shouldDirty: false });
    if (!pppoePassDirty.current) setValue("pppoe_password", largo, { shouldDirty: false });
    if (!acsSerialDirty.current) setAcsSerial(largo);
    // rc113 — si ByteOLT ya conoce la MAC de la ONU, autollenar onu_mac
    // (el SSID en ByteACS sale de la MAC; sin ella caería al serial)
    const oSel =
      onusHuerfanas.find((o) => o.serial === onuSerialSel) ??
      onusSinCliente.find((o) => o.serial === onuSerialSel);
    if (oSel?.mac && !String(onuMac ?? "").trim()) {
      setValue("fiber_detalle.onu_mac", fmtMac(oSel.mac), { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onuSerialSel, tipoServicio]);

  // rc111 — debounce de la consulta ACS mientras el operador teclea/escanea
  useEffect(() => {
    const t = setTimeout(() => setAcsSerialQuery(acsSerial.trim().toUpperCase()), 600);
    return () => clearTimeout(t);
  }, [acsSerial]);

  // rc112 — pre-registro: el serial tecleado pre-llena PPPoE + módem ACS, y al
  // cambiar la OLT destino se limpian plantilla/perfil (son por-OLT)
  useEffect(() => {
    if (!preMode || tipoServicio !== "fiber") return;
    const largo = serialLargo(preSerial.trim());
    if (!/^[0-9A-F]{12,16}$/i.test(largo)) return;
    if (!pppoeUserDirty.current) setValue("pppoe_user", largo, { shouldDirty: false });
    if (!pppoePassDirty.current) setValue("pppoe_password", largo, { shouldDirty: false });
    if (!acsSerialDirty.current) setAcsSerial(largo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSerial, preMode, tipoServicio]);

  useEffect(() => {
    setTemplateSel("");
    setPerfilSel("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOltId]);

  // rc112 — activar pre-registro suelta la ONU elegida (modos excluyentes)
  useEffect(() => {
    if (preMode) {
      setOnuSerialSel("");
      setMatchMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preMode]);

  // rc118 — activar MATCH suelta la ONU elegida y el pre-registro
  useEffect(() => {
    if (matchMode) {
      setOnuSerialSel("");
      setPreMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchMode]);

  // rc118 — MATCH: la serie tecleada pre-llena PPPoE (las credenciales REALES
  // del módem — por convención user = pass = serie larga; edítalas si el
  // cliente importado usa otras. NO se manda nada a ByteACS).
  useEffect(() => {
    if (!matchMode || tipoServicio !== "fiber") return;
    const largo = serialLargo(matchSerial.trim());
    if (!/^[0-9A-F]{12,16}$/i.test(largo)) return;
    if (!pppoeUserDirty.current) setValue("pppoe_user", largo, { shouldDirty: false });
    if (!pppoePassDirty.current) setValue("pppoe_password", largo, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchSerial, matchMode, tipoServicio]);

  // MAC → PPPoE user/pass exactamente como se escribe (si no están dirty).
  // No normaliza: respeta ":" y mayúsculas del UpperInput → "CC:2D:E0:11:4F:8B".
  // rc125 — SOLO WIRELESS. En fibra el PPPoE viene SIEMPRE de la SERIE (rc109):
  // el guardia viejo solo cubría la ONU elegida de la lista, y en PRE-REGISTRO /
  // MATCH (onuSerialSel vacío) la MAC capturada de la etiqueta pisaba el PPPoE
  // (alta salía con user "14:65:6A:95:AC:F6" hacia router y ByteACS).
  useEffect(() => {
    if (tipoServicio !== "wireless") return;
    const macRaw = cpeMac;
    if (!macRaw) return;
    const mac = (macRaw as string).trim();
    if (mac.length < 6) return; // espera a tener una MAC razonable
    if (!pppoeUserDirty.current) setValue("pppoe_user", mac, { shouldDirty: false });
    if (!pppoePassDirty.current) setValue("pppoe_password", mac, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpeMac, onuMac, tipoServicio]);

  // Next IP preview → ip_asignada (si no está dirty)
  useEffect(() => {
    if (!nextIpData?.ip) return;
    if (!ipAsignadaDirty.current) setValue("ip_asignada", nextIpData.ip, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextIpData?.ip]);

  // ip_asignada → cpe_ip_management / onu_ip_management (si no están dirty)
  useEffect(() => {
    if (!ipAsignada) return;
    if (tipoServicio === "wireless" && !cpeMgmtDirty.current) {
      setValue("wireless_detalle.cpe_ip_management", ipAsignada, { shouldDirty: false });
    } else if (tipoServicio === "fiber" && !onuMgmtDirty.current) {
      setValue("fiber_detalle.onu_ip_management", ipAsignada, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipAsignada, tipoServicio]);

  // Si cambia router y la red actual no pertenece, resetea red + ip
  useEffect(() => {
    const red = redesData?.items.find((r) => r.id === selectedRedId);
    if (red && red.router_id !== selectedRouterId) {
      setValue("red_id", "");
      setValue("ip_asignada", "");
      ipAsignadaDirty.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouterId]);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      reset({
        ...DEFAULTS,
        cliente_id: clientePreselectId ?? "",
        // rc145 — prellenado (convertir prospecto): GPS + referencia
        ...(initial?.latitud != null ? { latitud: initial.latitud } : {}),
        ...(initial?.longitud != null ? { longitud: initial.longitud } : {}),
        ...(initial?.ubicacion_referencia ? { ubicacion_referencia: initial.ubicacion_referencia } : {}),
        ...(initial?.notas_internas ? { notas_internas: initial.notas_internas } : {}),
      });
      if (initial?.tipoServicio) setTipoFiltro(initial.tipoServicio);
      setTab("cliente-plan");
      setError(null);
      setRevealedPassword(null);
      setOnuSerialSel("");
      setTemplateSel("");
      setPerfilSel("");
      setAcsSerial("");
      setAcsSerialQuery("");
      setWifiPass("");
      setSsidCustom("");
      setShowAlmacen(false);
      setShowCaptura(false);
      acsSerialDirty.current = false;
      setPreMode(false);
      setPreSerial("");
      setPreOltId("");
      setMatchMode(false);
      setMatchSerial("");
      pppoeUserDirty.current = false;
      pppoePassDirty.current = false;
      ipAsignadaDirty.current = false;
      cpeMgmtDirty.current = false;
      onuMgmtDirty.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientePreselectId, reset]);

  const createMut = useCreateContrato();

  // rc145 — datos para el resumen lateral y "misma ubicación que el domicilio"
  const { data: clienteSel } = useCliente(selectedClienteId || null);
  const { data: cfgFact } = useConfigByGrupo("facturacion");
  const diasAnticipacion = Number(cfgFact?.find((c) => c.key === "facturacion.dias_anticipacion")?.value ?? 5);
  const { data: cfgCob } = useConfigByGrupo("cobranza");
  const diasGraciaDefault = Number(cfgCob?.find((c) => c.key === "cobranza.dias_gracia")?.value ?? 5);
  const latW = watch("latitud"); const lngW = watch("longitud");
  const fechaCorteW = watch("fecha_corte");
  const precioOverrideW = watch("precio_override");
  const graciaW = watch("dias_gracia_override");
  const equipoDescW = watch("equipo_descripcion");
  const equipoModW = watch("equipo_modalidad");
  const costoInstW = watch("equipo_costo_instalacion");
  const pppoeUserW = watch("pppoe_user");
  const [gpsMsg, setGpsMsg] = useState<string | null>(null);
  const [mapsLink, setMapsLink] = useState("");
  function setCoords(lat: number, lng: number, origen: string) {
    setValue("latitud", Number(lat.toFixed(7)), { shouldDirty: true, shouldValidate: true });
    setValue("longitud", Number(lng.toFixed(7)), { shouldDirty: true, shouldValidate: true });
    setGpsMsg(origen);
  }
  function usarMiGps() {
    if (!navigator.geolocation) { setGpsMsg("Este navegador no tiene GPS disponible."); return; }
    setGpsMsg("Obteniendo ubicación…");
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords(pos.coords.latitude, pos.coords.longitude, `GPS del dispositivo (±${Math.round(pos.coords.accuracy)} m)`),
      (err) => setGpsMsg(err.code === 1 ? "Permiso de ubicación denegado." : "No se pudo obtener la ubicación."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  }
  function pegarEnlaceMaps() {
    const c = parseCoords(mapsLink);
    if (!c) { setGpsMsg("No encontré coordenadas en ese enlace. Pega el enlace de Google/Apple Maps o 'lat, lng'."); return; }
    setCoords(c.lat, c.lng, "Tomada del enlace de Maps");
    setMapsLink("");
  }
  const domicilioLat = clienteSel?.direccion_instalacion?.latitud;
  const domicilioLng = clienteSel?.direccion_instalacion?.longitud;
  function mismaQueDomicilio() {
    if (domicilioLat == null || domicilioLng == null) { setGpsMsg("El domicilio del cliente no tiene coordenadas."); return; }
    setCoords(Number(domicilioLat), Number(domicilioLng), "Misma que el domicilio del cliente");
  }
  const ubicacionSection = (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <MapPin size={12} /> Ubicación de la instalación
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Latitud</Label>
          <Input {...register("latitud")} placeholder="20.3889412" inputMode="decimal" />
          {errors.latitud && <span className="text-xs text-destructive">{String(errors.latitud.message)}</span>}
        </div>
        <div>
          <Label>Longitud</Label>
          <Input {...register("longitud")} placeholder="-99.9954120" inputMode="decimal" />
          {errors.longitud && <span className="text-xs text-destructive">{String(errors.longitud.message)}</span>}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={usarMiGps}>
          <Crosshair size={12} className="mr-1" /> Usar mi GPS
        </Button>
        <Button
          type="button" variant="outline" size="sm" onClick={mismaQueDomicilio}
          disabled={!selectedClienteId || domicilioLat == null}
          title={domicilioLat == null ? "El domicilio del cliente no tiene coordenadas" : undefined}
        >
          <User size={12} className="mr-1" /> Misma que el domicilio
        </Button>
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          value={mapsLink} onChange={(e) => setMapsLink(e.target.value)}
          placeholder="Pega un enlace de Google/Apple Maps o 'lat, lng'"
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={pegarEnlaceMaps} disabled={!mapsLink.trim()}>
          <Link2 size={12} className="mr-1" /> Tomar
        </Button>
      </div>
      {gpsMsg && <p className="mt-1 text-[10.5px] text-muted-foreground">{gpsMsg}</p>}
      <div className="mt-2">
        <Label>Referencia</Label>
        <Input {...register("ubicacion_referencia")} placeholder="Portón negro, segunda casa después de la tienda" />
      </div>
    </div>
  );

  // rc145 — resumen lateral (se llena conforme avanzas)
  const confirmCliente = (clientesData?.items ?? []).find((c) => c.id === selectedClienteId);
  const corteNum = Number(fechaCorteW) || 1;
  const corteDate = proximoCorte(corteNum);
  const emisionDate = new Date(corteDate); emisionDate.setDate(emisionDate.getDate() - diasAnticipacion);
  const routerSel = (routersData?.items ?? []).find((r) => r.id === selectedRouterId);
  const redSel = (redesData?.items ?? []).find((r) => r.id === selectedRedId);
  const precioMes = precioOverrideW !== "" && precioOverrideW != null
    ? Number(precioOverrideW) : selectedPlan ? Number(selectedPlan.precio) : null;
  const resumenRows: { k: string; v: React.ReactNode; falta?: boolean }[] = [
    { k: "Cliente", v: confirmCliente ? `${confirmCliente.codigo} · ${confirmCliente.razon_social}` : "— falta elegir", falta: !confirmCliente },
    { k: "Servicio", v: selectedPlan ? `${selectedPlan.tipo_servicio === "fiber" ? "Fibra" : "Wireless"} · ${selectedPlan.nombre} · ${precioMes != null ? formatMXN(precioMes) : ""}/mes` : "— falta elegir plan", falta: !selectedPlan },
    { k: "Router / red", v: routerSel ? `${routerSel.nombre}${redSel ? ` · ${redSel.nombre}` : " · falta red"}` : "— falta elegir", falta: !routerSel || !redSel },
    { k: "IP", v: ipAsignada || (nextIpData?.ip ? `${nextIpData.ip} (auto)` : "— se asigna al elegir red"), falta: !ipAsignada && !nextIpData?.ip },
    { k: "PPPoE", v: pppoeUserW || "auto al crear" },
    { k: "Equipo", v: [equipoDescW, equipoModW === "comodato" ? "comodato" : "propiedad del cliente"].filter(Boolean).join(" · ") || "—" },
    { k: "Corte / gracia", v: `Día ${corteNum} · ${graciaW !== "" && graciaW != null ? graciaW : diasGraciaDefault} días${graciaW === "" || graciaW == null ? " (default)" : ""}` },
    { k: "Primera mensualidad", v: `${fmtFecha(corteDate)} · se emite ${fmtFecha(emisionDate)}` },
    { k: "Instalación", v: Number(costoInstW) > 0 ? `${formatMXN(Number(costoInstW))} · se confirma al crear` : "sin cargo (se confirma al crear)" },
    { k: "Ubicación", v: latW !== "" && latW != null && lngW !== "" && lngW != null ? "capturada" : "sin capturar" },
  ];
  const resumenPanel = (
    <div className="rounded-md border bg-muted/20 p-3 text-xs">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Resumen</div>
      <dl className="flex flex-col gap-1.5">
        {resumenRows.map((r) => (
          <div key={r.k}>
            <dt className="text-[10.5px] text-muted-foreground">{r.k}</dt>
            <dd className={cn("truncate", r.falta ? "font-medium text-destructive" : "")} title={typeof r.v === "string" ? r.v : undefined}>{r.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  // rc110 — fila del picker de ONUs (compartida: huérfanas y ya-aprovisionadas)
  function renderOnuRow(o: OnuHuerfana, aprovisionada = false) {
    const sel = o.serial === onuSerialSel;
    const rxWarn = o.rx_dbm != null && o.rx_dbm < -25;
    return (
      <button
        type="button"
        key={o.serial}
        onClick={() => {
          setPreMode(false); // rc112 — elegir de la lista apaga el pre-registro
          setMatchMode(false); // rc118 — y el match
          setOnuSerialSel(sel ? "" : o.serial);
        }}
        className={cn(
          "flex w-full items-center gap-3 border-b px-3 py-2 text-left text-xs last:border-b-0",
          sel ? "bg-primary/10" : "hover:bg-primary/5",
        )}
      >
        <span
          className={cn(
            "relative h-3.5 w-3.5 flex-none rounded-full border-2",
            sel ? "border-primary" : "border-muted-foreground/40",
          )}
        >
          {sel && <span className="absolute inset-[2px] rounded-full bg-primary" />}
        </span>
        <span className="min-w-0 flex-1">
          {/* rc109 — la forma LARGA/hex al frente (la de la etiqueta) */}
          <span className="block truncate font-mono text-[11.5px]">
            {serialLargo(o.serial)}
          </span>
          <span className="block truncate font-mono text-[10px] text-muted-foreground">
            {[
              serialLargo(o.serial) !== o.serial ? o.serial : null,
              o.vendor, o.descripcion,
              aprovisionada && o.vlan != null ? `VLAN ${o.vlan}` : null,
            ].filter(Boolean).join(" · ") || "—"}
          </span>
        </span>
        <span className="flex-none text-right font-mono text-[10.5px] text-muted-foreground">
          <span className="block">{o.olt ?? "?"}</span>
          <span className="block text-[9.5px]">PON {o.pon ?? "?"}</span>
        </span>
        <span
          className={cn(
            "w-16 flex-none text-right font-mono text-[10.5px]",
            rxWarn
              ? "text-amber-600 dark:text-amber-400"
              : "text-emerald-600 dark:text-emerald-400",
          )}
        >
          {o.rx_dbm != null ? `${o.rx_dbm} dBm` : "—"}
        </span>
      </button>
    );
  }

  const onSubmit: SubmitHandler<ContratoCreateInput> = async (values) => {
    setError(null);
    try {
      const payload: Partial<ContratoCreateInput> = { ...values };
      if (tipoServicio === "wireless") {
        delete payload.fiber_detalle;
        delete payload.byteolt;
        delete payload.byteacs;
        payload.wireless_detalle = clean(values.wireless_detalle);
      } else if (tipoServicio === "fiber") {
        delete payload.wireless_detalle;
        // rc108 — fibra SIEMPRE vía ByteOLT: sin integración no hay servicio
        if (byteoltEnabled === false) {
          setError(
            "El servicio de fibra requiere la integración ByteOLT activa (Ajustes › Integraciones).",
          );
          return;
        }
        const fiber = (clean(values.fiber_detalle) ?? {}) as Record<string, unknown>;
        if (matchMode) {
          // rc118 — MATCH: ya aprovisionada Y ligada (cliente importado).
          // Solo se re-liga el contrato en ByteOLT (cero CLI) y NO se manda
          // nada a ByteACS — el módem ya está configurado.
          const mserial = serialLargo(matchSerial.trim());
          if (!/^[0-9A-F]{12,16}$/i.test(mserial)) {
            setError("Escribe (o escanea) la serie de la ONU ya aprovisionada.");
            setTab("detalle");
            return;
          }
          payload.fiber_detalle = {
            ...fiber,
            onu_serial: mserial,
          } as ContratoCreateInput["fiber_detalle"];
          payload.byteolt = { serial: mserial, solo_match: true };
          delete payload.byteacs;
        } else if (preMode) {
          // rc112 — PRE-registro: la OLT aún no ve la ONU
          const pserial = serialLargo(preSerial.trim());
          if (!/^[0-9A-F]{12,16}$/i.test(pserial)) {
            setError("Escribe (o escanea) la serie de la ONU a pre-registrar.");
            setTab("detalle");
            return;
          }
          if (!preOltId) {
            setError("Elige la OLT destino del pre-registro.");
            setTab("detalle");
            return;
          }
          if (preOlt?.vendor === "vsol" && !templateSel && (plantillas?.length ?? 0) > 0) {
            setError("Elige la plantilla de servicio (VLANs) para esa OLT.");
            setTab("detalle");
            return;
          }
          payload.fiber_detalle = {
            ...fiber,
            onu_serial: pserial,
            ...(preOlt?.nombre ? { olt_puerto: preOlt.nombre } : {}),
          } as ContratoCreateInput["fiber_detalle"];
          payload.byteolt = {
            serial: pserial,
            pre_registro: true,
            olt_id: preOltId,
            ...(templateSel ? { template_id: templateSel } : {}),
            ...(perfilSel ? { line_profile: perfilSel } : {}),
          };
        } else {
          if (!onuSerialSel) {
            setError(
              "Elige la ONU en la pestaña Detalle ONU (o usa el pre-registro si la OLT aún no la ve).",
            );
            setTab("detalle");
            return;
          }
          if (!onuYaAprov && onuSel?.olt_vendor === "vsol" && !templateSel && (plantillas?.length ?? 0) > 0) {
            setError("Elige la plantilla de servicio (VLANs) para esta OLT.");
            setTab("detalle");
            return;
          }
          payload.fiber_detalle = {
            ...fiber,
            // rc109 — se guarda la forma larga/hex (la que siempre se ha
            // tecleado en ByteISP; ByteOLT liga por serie normalizando ambas)
            onu_serial: serialLargo(onuSerialSel),
            ...(onuVendorGuess ? { onu_vendor: onuVendorGuess } : {}),
            ...(onuSel?.olt
              ? { olt_puerto: `${onuSel.olt} · PON ${onuSel.pon ?? "?"}` }
              : {}),
          } as ContratoCreateInput["fiber_detalle"];
          payload.byteolt = onuYaAprov
            ? { serial: onuSerialSel, ya_aprovisionada: true }
            : {
                serial: onuSerialSel,
                ...(templateSel ? { template_id: templateSel } : {}),
                ...(perfilSel ? { line_profile: perfilSel } : {}),
              };
        }
        // rc111 — serial del módem para ByteACS (si difiere del de la ONU o
        // se escaneó); el backend usa el de la ONU si no viene.
        // rc118 — en MATCH nunca viaja byteacs (cero órdenes al ACS).
        // rc121 — clave WiFi opcional (8-63): ByteACS la aplica al 1er Inform.
        if (!matchMode && byteacsEnabled && wifiPass.trim() && wifiPass.trim().length < 8) {
          setError("La contraseña WiFi debe tener al menos 8 caracteres (o déjala vacía = clave de fábrica).");
          setTab("detalle");
          return;
        }
        if (!matchMode && byteacsEnabled && acsSerial.trim()) {
          payload.byteacs = {
            serial: acsSerial.trim().toUpperCase(),
            ...(wifiPass.trim().length >= 8 ? { wifi_pass: wifiPass.trim() } : {}),
            ...(ssidCustom.trim() ? { ssid: ssidCustom.trim() } : {}),
          };
        } else {
          delete payload.byteacs;
        }
      } else {
        delete payload.wireless_detalle;
        delete payload.fiber_detalle;
        delete payload.byteolt;
        delete payload.byteacs;
      }
      const cleaned = clean(payload);
      // rc124 — ANTES de escribir en sistema: modal de confirmación con la
      // EMPRESA del cliente (último filtro anti-equivocación) + monto de la
      // factura de instalación, prellenado con el costo capturado en Equipo.
      setInstMonto(String(values.equipo_costo_instalacion ?? 0));
      setConfirmData(cleaned as ContratoCreateInput);
    } catch (e) {
      setError((e as ApiError).message ?? "No se pudo crear el contrato");
    }
  };

  // rc124 — confirmación final: adjunta el monto de instalación y crea
  async function confirmCreate() {
    if (!confirmData) return;
    const monto = Number(instMonto);
    if (Number.isNaN(monto) || monto < 0) {
      setError("El monto de instalación no es válido.");
      return;
    }
    setError(null);
    try {
      const payload: ContratoCreateInput = {
        ...confirmData,
        ...(monto > 0 ? { factura_instalacion_monto: monto } : {}),
      };
      const saved = await createMut.mutateAsync(payload);
      setConfirmData(null);
      setRevealedPassword(saved);
    } catch (e) {
      setConfirmData(null);
      setError((e as ApiError).message ?? "No se pudo crear el contrato");
    }
  }

  function handlePasswordConfirmed() {
    const contratoId = revealedPassword!.id;
    setRevealedPassword(null);
    onOpenChange(false);
    onCreated?.(contratoId);
    navigate(`/contratos/${contratoId}`);
  }

  const submitting = createMut.isPending;

  // Opciones para comboboxes
  const clienteOptions: ComboboxOption[] = useMemo(
    () =>
      (clientesData?.items ?? []).map((c) => ({
        value: c.id,
        label: `${c.razon_social}`,
        hint: `${c.codigo} · ${c.rfc}`,
      })),
    [clientesData]
  );
  const planOptions: ComboboxOption[] = useMemo(
    () =>
      (planesData?.items ?? [])
        .filter((p) => p.activo)
        // F108/rc109 — filtra por el tipo elegido (ya no existe "Todos")
        .filter((p) => p.tipo_servicio === tipoFiltro)
        // rc108 — sin integración ByteOLT no hay servicio de fibra disponible
        .filter((p) => byteoltEnabled === false ? p.tipo_servicio !== "fiber" : true)
        .map((p) => ({
          value: p.id,
          label: p.nombre,
          hint: `${p.tipo_servicio} · ${p.velocidad_down_mbps}/${p.velocidad_up_mbps} Mbps · ${formatMXN(p.precio)}`,
        })),
    [planesData, tipoFiltro, byteoltEnabled]
  );
  const routerOptions: ComboboxOption[] = useMemo(
    () =>
      (routersData?.items ?? [])
        .filter((r) => r.activo)
        .map((r) => ({
          value: r.id,
          label: r.nombre,
          hint: r.ip_address,
        })),
    [routersData]
  );
  const redOptions: ComboboxOption[] = useMemo(
    () =>
      redesFiltradas.map((r) => ({
        value: r.id,
        label: r.nombre,
        hint: `${r.cidr}${r.vlan_id ? ` · VLAN ${r.vlan_id}` : ""}`,
      })),
    [redesFiltradas]
  );
  const sitioWirelessOptions: ComboboxOption[] = useMemo(
    () =>
      (sitiosData?.items ?? [])
        .filter((s) => s.tipo === "wireless_tower" || s.tipo === "nodo")
        .map((s) => ({
          value: s.id,
          label: s.nombre,
          hint: [s.municipio, s.estado].filter(Boolean).join(", "),
        })),
    [sitiosData]
  );
  const sitioFiberOptions: ComboboxOption[] = useMemo(
    () =>
      (sitiosData?.items ?? [])
        .filter((s) => s.tipo === "fiber_olt")
        .map((s) => ({
          value: s.id,
          label: s.nombre,
          hint: [s.municipio, s.estado].filter(Boolean).join(", "),
        })),
    [sitiosData]
  );

  return (
    <>
      <Dialog open={open && !revealedPassword && !confirmData} onOpenChange={onOpenChange} size="xl">
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <DialogHeader>
            <DialogTitle>Nuevo contrato</DialogTitle>
            <DialogDescription>
              Auto-asigna IP del pool de la red seleccionada. Usuario/password PPPoE se
              pre-llenan con la MAC del equipo (editables).
            </DialogDescription>
          </DialogHeader>

          <DialogContent>
            {/* rc145 — banner de pestañas con error (aparece al intentar crear) */}
            {isSubmitted && tabsConError.length > 0 && (
              <div role="alert" className="mb-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div className="flex flex-col gap-0.5">
                  {tabsConError.map((t) => (
                    <span key={t}>
                      Faltan {errorsByTab[t].length} dato(s) en{" "}
                      <button type="button" className="font-semibold underline" onClick={() => setTab(t)}>
                        {t === "detalle" ? (tipoServicio === "fiber" ? "Detalle ONU" : "Detalle wireless") : TAB_LABEL[t]}
                      </button>
                      : {Array.from(new Set(errorsByTab[t])).join(", ")}.
                    </span>
                  ))}
                  <span className="text-[10.5px] opacity-80">El contrato no se crea hasta completarlos.</span>
                </div>
              </div>
            )}
            <div className="md:grid md:grid-cols-[minmax(0,1fr)_236px] md:gap-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="cliente-plan"><User size={12} className="mr-1" />Cliente & Plan<TabBadge t="cliente-plan" /></TabsTrigger>
                <TabsTrigger value="red"><NetworkIcon size={12} className="mr-1" />Router & Red<TabBadge t="red" /></TabsTrigger>
                {tipoServicio && (
                  <TabsTrigger value="detalle">
                    <Wrench size={12} className="mr-1" />
                    {tipoServicio === "fiber" ? "Detalle ONU" : "Detalle wireless"}<TabBadge t="detalle" />
                  </TabsTrigger>
                )}
                <TabsTrigger value="pppoe"><KeyRound size={12} className="mr-1" />PPPoE<TabBadge t="pppoe" /></TabsTrigger>
                <TabsTrigger value="config"><Settings size={12} className="mr-1" />Facturación<TabBadge t="config" /></TabsTrigger>
              </TabsList>

              {/* === CLIENTE & PLAN === */}
              <TabsContent value="cliente-plan">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Cliente *</Label>
                    <Combobox
                      options={clienteOptions}
                      value={selectedClienteId}
                      onChange={(v) => setValue("cliente_id", v, { shouldValidate: true })}
                      placeholder="Buscar cliente por razón social, código o RFC…"
                      disabled={!!clientePreselectId}
                    />
                    {errors.cliente_id && <span className="text-xs text-destructive">{errors.cliente_id.message}</span>}
                  </div>
                  {/* F108 — Selector tipo servicio para filtrar planes */}
                  <div>
                    <Label>Tipo de servicio</Label>
                    <div className="grid grid-cols-2 gap-1 rounded-md bg-muted/40 p-1">
                      {(["wireless", "fiber"] as const).map((t) => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => {
                            setTipoFiltro(t);
                            // Si el plan seleccionado no es del tipo elegido, limpiar
                            if (selectedPlan && selectedPlan.tipo_servicio !== t) {
                              setValue("plan_id", "", { shouldValidate: false });
                            }
                          }}
                          className={cn(
                            "rounded px-2 py-1.5 text-xs font-medium transition-colors capitalize",
                            tipoFiltro === t
                              ? "bg-background text-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {t === "wireless" ? "Wireless" : "Fibra"}
                        </button>
                      ))}
                    </div>
                    {/* rc108 — fibra bloqueada sin integración ByteOLT */}
                    {byteoltEnabled === false && tipoFiltro !== "wireless" && (
                      <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                        Servicio de fibra no disponible: la integración ByteOLT está
                        desactivada (Ajustes › Integraciones). Solo se listan planes wireless.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Plan *</Label>
                    <Combobox
                      options={planOptions}
                      value={selectedPlanId}
                      onChange={(v) => setValue("plan_id", v, { shouldValidate: true })}
                      placeholder={`Buscar plan de ${tipoFiltro === "fiber" ? "fibra" : "wireless"}…`}
                    />
                    {errors.plan_id && <span className="text-xs text-destructive">{errors.plan_id.message}</span>}
                    {selectedPlan && (
                      <div className="mt-1 rounded-md border bg-muted/20 p-2 text-xs">
                        <div><strong>{selectedPlan.nombre}</strong></div>
                        <div className="text-muted-foreground">
                          Tipo: {selectedPlan.tipo_servicio} · {selectedPlan.velocidad_down_mbps}/{selectedPlan.velocidad_up_mbps} Mbps · {formatMXN(selectedPlan.precio)}/mes
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* === ROUTER & RED === */}
              <TabsContent value="red">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Router *</Label>
                    <Combobox
                      options={routerOptions}
                      value={selectedRouterId}
                      onChange={(v) => setValue("router_id", v, { shouldValidate: true })}
                      placeholder="Buscar router por nombre o IP…"
                    />
                    {errors.router_id && <span className="text-xs text-destructive">{errors.router_id.message}</span>}
                  </div>
                  <div>
                    <Label>Red *</Label>
                    <Combobox
                      options={redOptions}
                      value={selectedRedId}
                      onChange={(v) => {
                        setValue("red_id", v, { shouldValidate: true });
                        // Al cambiar red, la próxima IP se auto-llena cuando llegue nextIpData
                        ipAsignadaDirty.current = false;
                        setValue("ip_asignada", "");
                      }}
                      placeholder={!selectedRouterId ? "Primero selecciona router" : "Buscar red…"}
                      disabled={!selectedRouterId}
                      emptyLabel={!selectedRouterId ? "Selecciona un router primero" : "Este router no tiene redes asignadas"}
                    />
                    {errors.red_id && <span className="text-xs text-destructive">{errors.red_id.message}</span>}
                  </div>
                  <div>
                    <Label className="flex items-center justify-between gap-2">
                      <span>IP asignada (auto-calculada del pool)</span>
                      {nextIpData?.ip && !ipAsignadaDirty.current && (
                        <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                          auto · {nextIpData.ip}
                        </span>
                      )}
                    </Label>
                    <Input
                      {...register("ip_asignada", {
                        onChange: () => { ipAsignadaDirty.current = true; },
                      })}
                      placeholder={nextIpData?.ip ?? "Selecciona red para calcular"}
                    />
                    {errors.ip_asignada && <span className="text-xs text-destructive">{errors.ip_asignada.message}</span>}
                  </div>
                </div>
              </TabsContent>

              {/* === PPPoE === */}
              <TabsContent value="pppoe">
                <div className="grid grid-cols-1 gap-3">
                  <div className="rounded-md border bg-muted/20 p-3 text-xs">
                    Si dejas estos campos vacíos, se auto-generan (user = <code>{`{código}-{n}`}</code>, password = 14 chars aleatorios).
                    Al escribir la MAC del equipo en "Detalle técnico", ambos se pre-rellenan automáticamente (puedes sobreescribir).
                  </div>
                  <div>
                    <Label>Usuario PPPoE</Label>
                    <Input
                      {...register("pppoe_user", {
                        onChange: () => { pppoeUserDirty.current = true; },
                      })}
                      placeholder="Vacío = auto-generado · Se pre-llena con MAC si hay"
                    />
                    {errors.pppoe_user && <span className="text-xs text-destructive">{errors.pppoe_user.message}</span>}
                  </div>
                  <div>
                    <Label>Password PPPoE</Label>
                    <Input
                      type="text"
                      {...register("pppoe_password", {
                        onChange: () => { pppoePassDirty.current = true; },
                      })}
                      placeholder="Vacío = 14 chars aleatorios · Se pre-llena con MAC si hay"
                    />
                    {errors.pppoe_password && <span className="text-xs text-destructive">{errors.pppoe_password.message}</span>}
                  </div>
                </div>
              </TabsContent>

              {/* === CONFIG === */}
              <TabsContent value="config">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Día de corte *</Label>
                    <Select {...register("fecha_corte")}>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>Día {d} de cada mes</option>
                      ))}
                    </Select>
                    <span className="text-[10px] text-muted-foreground">Solo días 1–28 (evita conflicto con febrero).</span>
                  </div>
                  <div>
                    <Label>Descuento %</Label>
                    <Input type="number" step="0.01" min={0} max={100} {...register("descuento_pct")} />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium">
                      <input
                        type="checkbox"
                        checked={useOverridePrecio}
                        onChange={(e) => togglePrecioOverride(e.target.checked)}
                      />
                      Usar precio distinto al del plan
                    </label>
                    <Input
                      type="number" step="0.01" min={0.01}
                      {...register("precio_override")}
                      disabled={!useOverridePrecio}
                      placeholder={
                        useOverridePrecio
                          ? "Ej. 300.00"
                          : selectedPlan
                            ? `Usa plan: ${formatMXN(selectedPlan.precio)}`
                            : "Usa precio del plan"
                      }
                      className="mt-1"
                    />
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      Activa solo si este cliente tiene precio negociado distinto al plan base.
                    </p>
                    {errors.precio_override && useOverridePrecio && (
                      <span className="text-xs text-destructive">
                        {errors.precio_override.message as string}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium">
                      <input
                        type="checkbox"
                        checked={useOverrideGracia}
                        onChange={(e) => toggleGraciaOverride(e.target.checked)}
                      />
                      Usar días de gracia distintos al default
                    </label>
                    <Input
                      type="number" min={0} max={60}
                      {...register("dias_gracia_override")}
                      disabled={!useOverrideGracia}
                      placeholder={useOverrideGracia ? "Ej. 3" : "Default sistema: 5"}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Notas (visibles al cliente)</Label>
                    <Textarea rows={2} {...register("notas")} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notas internas (privadas)</Label>
                    <Textarea rows={2} {...register("notas_internas")} />
                  </div>

                  {/* Flags especiales */}
                  <div className="col-span-2 mt-2 rounded-md border bg-muted/20 p-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase text-muted-foreground">
                      Exenciones
                    </div>
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        {...register("exento_facturacion")}
                        className="mt-0.5 h-3 w-3"
                      />
                      <div>
                        <div>Exento de facturación (cliente gratis)</div>
                        <div className="text-[10px] text-muted-foreground">
                          No se generan facturas ni entra a cobranza automática.
                          Útil para familiares, socios, cuentas de prueba.
                        </div>
                      </div>
                    </label>
                    <label className="mt-2 flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        {...register("exento_monitoreo")}
                        className="mt-0.5 h-3 w-3"
                      />
                      <div>
                        <div>Excluir de monitoreo ByteNMS</div>
                        <div className="text-[10px] text-muted-foreground">
                          No se emiten eventos a ByteNMS (icmp/snmp) para este contrato.
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* F92 — Equipo en comodato vs propiedad */}
                  <div className="col-span-2 mt-2 rounded-md border bg-muted/20 p-3">
                    <div className="mb-2 text-[10px] font-semibold uppercase text-muted-foreground">
                      Equipo (CPE/router/antena)
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label>Modalidad</Label>
                        <Select {...register("equipo_modalidad")}>
                          <option value="comodato">Comodato (ISP recupera al cancelar)</option>
                          <option value="propiedad_cliente">Propiedad del cliente (no se recupera)</option>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Descripción</Label>
                        <Input
                          {...register("equipo_descripcion")}
                          placeholder="Ej. TP-Link Archer C20 + antena LBE-5AC-Gen2"
                        />
                      </div>
                      <div>
                        <Label>Costo instalación (MXN)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          {...register("equipo_costo_instalacion")}
                          placeholder="0"
                        />
                        <span className="text-[10px] text-muted-foreground">
                          Cobro único al instalar (si propiedad, suele ser mayor).
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* === DETALLE WIRELESS === */}
              {tipoServicio === "wireless" && (
                <TabsContent value="detalle">
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs mb-3">
                    <strong>Solo la MAC es requerida</strong> para crear el contrato. Los demás
                    campos son inventario del equipo (modelo, serial, credenciales) y son útiles
                    pero opcionales.
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Campos destacados (MAC + Vendor) */}
                    <div className="col-span-2 grid grid-cols-2 gap-3 rounded-md border bg-primary/5 p-3">
                      <div>
                        <Label>MAC del CPE *</Label>
                        <UpperInput
                          control={control}
                          name="wireless_detalle.cpe_mac"
                          placeholder="AA:BB:CC:DD:EE:FF"
                        />
                        <span className="text-[10px] text-muted-foreground">
                          Pre-llena PPPoE user+pass automáticamente.
                        </span>
                      </div>
                      <div>
                        <Label>Vendor del CPE</Label>
                        <Select {...register("wireless_detalle.cpe_vendor")}>
                          <option value="">—</option>
                          {cpeVendorEnum.map((v) => (
                            <option key={v} value={v}>{CPE_VENDOR_LABEL[v]}</option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    {/* Resto (opcional pero visible) */}
                    <div>
                      <Label>Modelo</Label>
                      <Input {...register("wireless_detalle.cpe_modelo")} placeholder="CPE210, LHG60, etc." />
                    </div>
                    <div>
                      <Label>Serial</Label>
                      <Input {...register("wireless_detalle.cpe_serial")} />
                    </div>
                    <div>
                      <Label className="flex items-center justify-between gap-2">
                        <span>IP management</span>
                        {ipAsignada && !cpeMgmtDirty.current && (
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                            auto · {ipAsignada}
                          </span>
                        )}
                      </Label>
                      <Input
                        {...register("wireless_detalle.cpe_ip_management", {
                          onChange: () => { cpeMgmtDirty.current = true; },
                        })}
                        placeholder="Se auto-llena con IP asignada"
                      />
                    </div>
                    <div>
                      <Label>SSID de conexión</Label>
                      <Input {...register("wireless_detalle.ssid_conexion")} />
                    </div>
                    <div>
                      <Label>Frecuencia (MHz)</Label>
                      <Input type="number" {...register("wireless_detalle.frecuencia_mhz")} placeholder="5800" />
                    </div>
                    <div>
                      <Label>Polarización</Label>
                      <Select {...register("wireless_detalle.polarizacion")}>
                        <option value="">—</option>
                        {polarizacionEnum.map((p) => <option key={p} value={p}>{p}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Usuario admin CPE</Label>
                      <Input {...register("wireless_detalle.cpe_usuario_admin")} placeholder="admin" />
                    </div>
                    <div>
                      <Label>Password admin CPE</Label>
                      <Input type="password" {...register("wireless_detalle.cpe_password_admin")} />
                    </div>
                    <div className="col-span-2">
                      <Label>Sitio (torre/AP)</Label>
                      <Combobox
                        options={sitioWirelessOptions}
                        value={(watch("wireless_detalle.sitio_id") as string) ?? ""}
                        onChange={(v) => setValue("wireless_detalle.sitio_id", v)}
                        placeholder="Buscar torre o nodo…"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notas de instalación</Label>
                      <Textarea rows={2} {...register("wireless_detalle.notas_instalacion")} />
                    </div>
                  </div>
                  {ubicacionSection}

                </TabsContent>
              )}

              {/* === DETALLE FIBER — rc108: ONU vía ByteOLT === */}
              {tipoServicio === "fiber" && (
                <TabsContent value="detalle">
                  {byteoltEnabled === false ? (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                      <strong>Servicio de fibra no disponible.</strong> La integración con
                      ByteOLT está desactivada — actívala en Ajustes › Integraciones para
                      dar de alta contratos de fibra.
                    </div>
                  ) : (
                    <>
                      <div className="mb-2 flex items-center justify-between">
                        <Label className="mb-0">
                          ONU por autorizar{" "}
                          <span className="font-mono text-[10px] font-normal text-primary">
                            · en vivo desde ByteOLT
                          </span>
                        </Label>
                        <Button
                          type="button" variant="ghost" size="sm"
                          onClick={() => refetchOnus()}
                          disabled={onusLoading}
                        >
                          <RefreshCw size={12} className={onusLoading ? "animate-spin" : ""} />
                        </Button>
                      </div>
                      <div className="overflow-hidden rounded-md border">
                        <div className="border-b bg-muted/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                          Por autorizar · {onusHuerfanas.length}
                        </div>
                        {onusLoading && onusHuerfanas.length === 0 && onusSinCliente.length === 0 ? (
                          <p className="p-4 text-center text-xs text-muted-foreground">
                            Consultando ONUs en ByteOLT…
                          </p>
                        ) : onusHuerfanas.length === 0 ? (
                          <p className="p-3 text-center text-[11px] text-muted-foreground">
                            No hay ONUs por autorizar. Conecta la ONU del cliente a la
                            fibra y refresca — aparece en cuanto la OLT la ve.
                          </p>
                        ) : (
                          onusHuerfanas.map((o) => renderOnuRow(o))
                        )}
                        {/* rc110 — ya aprovisionadas desde ByteOLT, solo falta el contrato */}
                        {onusSinCliente.length > 0 && (
                          <>
                            <div className="border-b border-t bg-muted/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                              Ya aprovisionadas · sin cliente · {onusSinCliente.length}
                            </div>
                            {onusSinCliente.map((o) => renderOnuRow(o, true))}
                          </>
                        )}
                      </div>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        La serie elegida se guarda en el contrato y pre-llena PPPoE
                        user/pass. Señal en ámbar (&lt; −25 dBm) = revisar el enlace antes
                        de dar de alta. Las &quot;ya aprovisionadas&quot; solo se LIGAN
                        (el técnico las autorizó desde ByteOLT).
                      </p>

                      {/* ═══ rc112 — PRE-REGISTRO: la OLT aún no ve la ONU ═══ */}
                      <button
                        type="button"
                        onClick={() => setPreMode((v) => !v)}
                        className={cn(
                          "mt-2 w-full rounded-md border px-3 py-2 text-left text-xs font-medium",
                          preMode
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-primary/5",
                        )}
                      >
                        {preMode ? "● " : "○ "}¿La OLT aún no ve la ONU? Pre-registrar
                        serial — se autoriza sola en cuanto se conecte
                      </button>
                      {preMode && (
                        <div className="mt-2 rounded-md border border-primary/25 bg-primary/5 p-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Serie de la ONU (de la etiqueta)</Label>
                              <div className="flex gap-1.5">
                                <Input
                                  value={preSerial}
                                  onChange={(e) => setPreSerial(e.target.value.toUpperCase())}
                                  placeholder="48575443E677FEB7"
                                  className="font-mono"
                                />
                                <Button
                                  type="button" variant="outline" size="sm"
                                  onClick={() => setShowCaptura(true)}
                                >
                                  Escanear
                                </Button>
                              </div>
                            </div>
                            <div>
                              <Label>OLT destino</Label>
                              <Select
                                value={preOltId}
                                onChange={(e) => setPreOltId(e.target.value)}
                              >
                                <option value="">Selecciona la OLT…</option>
                                {(oltsData ?? [])
                                  .filter((o) => !o.read_only)
                                  .map((o) => (
                                    <option key={o.id} value={o.id}>
                                      {o.nombre}{o.modelo ? ` · ${o.modelo}` : ""}
                                    </option>
                                  ))}
                              </Select>
                            </div>
                            {preOltId && (
                              <>
                                <div>
                                  <Label>Plantilla de servicio (VLANs)</Label>
                                  <Select
                                    value={templateSel}
                                    onChange={(e) => setTemplateSel(e.target.value)}
                                  >
                                    <option value="">
                                      {preOlt?.vendor === "tplink"
                                        ? "TP-Link: las VLANs viven en el perfil — elige solo el perfil"
                                        : (plantillas?.length ?? 0) === 0
                                          ? "Sin plantillas en esta OLT (VLAN default)"
                                          : "Selecciona plantilla…"}
                                    </option>
                                    {(plantillas ?? []).map((t) => (
                                      <option key={t.id} value={t.id}>
                                        {t.name} — datos {t.vlan_datos}
                                        {t.vlan_aprov ? ` + aprov ${t.vlan_aprov}` : ""}
                                      </option>
                                    ))}
                                  </Select>
                                </div>
                                <div>
                                  <Label>Perfil ONU</Label>
                                  <Select
                                    value={perfilSel}
                                    onChange={(e) => setPerfilSel(e.target.value)}
                                  >
                                    <option value="">auto — default de la OLT</option>
                                    {(perfiles ?? []).map((p, i) => {
                                      const name = p.name ?? p.nombre ?? "";
                                      return name ? (
                                        <option key={`${name}-${i}`} value={name}>{name}</option>
                                      ) : null;
                                    })}
                                  </Select>
                                </div>
                              </>
                            )}
                          </div>
                          <p className="mt-2 text-[10.5px] leading-relaxed text-primary">
                            El contrato se crea de una vez y el pre-registro queda
                            <strong> esperando conexión</strong> en ByteOLT: el sondeo la
                            autoriza sola en cuanto la ONU aparezca en{" "}
                            <strong>{preOlt?.nombre ?? "la OLT elegida"}</strong> (sin
                            expiración). Si aparece en OTRA OLT, queda en espera con aviso.
                            Si se cancela el contrato, el pre-registro se borra solo.
                          </p>
                        </div>
                      )}

                      {/* ═══ rc118 — MATCH: ya aprovisionada Y LIGADA (importar) ═══ */}
                      <button
                        type="button"
                        onClick={() => setMatchMode((v) => !v)}
                        className={cn(
                          "mt-2 w-full rounded-md border px-3 py-2 text-left text-xs font-medium",
                          matchMode
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-primary/5",
                        )}
                      >
                        {matchMode ? "● " : "○ "}¿Ya está aprovisionada Y ligada desde
                        ByteOLT/ByteACS? Solo hacer MATCH — cero órdenes
                      </button>
                      {matchMode && (
                        <div className="mt-2 rounded-md border border-primary/25 bg-primary/5 p-3">
                          <Label>Serie de la ONU (de la etiqueta)</Label>
                          <div className="flex gap-1.5">
                            <Input
                              value={matchSerial}
                              onChange={(e) => setMatchSerial(e.target.value.toUpperCase())}
                              placeholder="48575443E677FEB7"
                              className="font-mono"
                            />
                            <Button
                              type="button" variant="outline" size="sm"
                              onClick={() => setShowCaptura(true)}
                            >
                              Escanear
                            </Button>
                          </div>
                          <p className="mt-2 text-[10.5px] leading-relaxed text-primary">
                            Para clientes <strong>importados</strong> (dados de alta directo
                            en ByteOLT/ByteACS): el contrato solo se <strong>re-liga</strong> a
                            la ONU en ByteOLT (cero CLI a la OLT) y <strong>NO se manda nada
                            a ByteACS</strong> — el módem sigue con su config actual.
                          </p>
                          <p className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-[10.5px] leading-relaxed text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                            ⚠ El PPPoE se pre-llena con la serie larga (tu convención). Si el
                            cliente usa OTRAS credenciales, captura las <strong>reales</strong> en
                            la pestaña PPPoE — un password distinto al del módem pisa el secret
                            del router y el cliente se cae.
                          </p>
                        </div>
                      )}

                      {/* rc110 — ya aprovisionada: solo se LIGA, sin plantilla ni CLI */}
                      {onuSel && onuYaAprov && (
                        <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-[11px] leading-relaxed text-emerald-600 dark:text-emerald-400">
                          <MapPin size={12} className="mt-0.5 flex-none" />
                          <span>
                            <strong className="font-mono">{serialLargo(onuSel.serial)}</strong>{" "}
                            ya está aprovisionada en{" "}
                            <strong>{onuSel.olt} · PON {onuSel.pon ?? "?"}</strong>
                            {onuSel.vlan != null ? <> (VLAN {onuSel.vlan})</> : null} — al
                            crear el contrato solo se LIGA el cliente en ByteOLT, sin tocar
                            la OLT. La serie queda en el detalle de fibra.
                          </span>
                        </div>
                      )}

                      {onuSel && !onuYaAprov && (
                        <div className="mt-3 grid grid-cols-2 gap-3 rounded-md border bg-muted/20 p-3">
                          <div>
                            <Label>Plantilla de servicio (VLANs)</Label>
                            <Select
                              value={templateSel}
                              onChange={(e) => setTemplateSel(e.target.value)}
                            >
                              <option value="">
                                {onuSel?.olt_vendor === "tplink"
                                  ? "TP-Link: las VLANs viven en el perfil — elige solo el perfil"
                                  : (plantillas?.length ?? 0) === 0
                                    ? "Sin plantillas en esta OLT (VLAN default)"
                                    : "Selecciona plantilla…"}
                              </option>
                              {(plantillas ?? []).map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name} — datos {t.vlan_datos}
                                  {t.vlan_aprov ? ` + aprov ${t.vlan_aprov}` : ""}
                                </option>
                              ))}
                            </Select>
                            <span className="text-[10px] text-muted-foreground">
                              Definidas en ByteOLT por OLT. En VSOL es obligatoria.
                            </span>
                          </div>
                          <div>
                            <Label>Perfil ONU</Label>
                            <Select
                              value={perfilSel}
                              onChange={(e) => setPerfilSel(e.target.value)}
                            >
                              <option value="">auto — default de la OLT</option>
                              {(perfiles ?? []).map((p, i) => {
                                const name = p.name ?? p.nombre ?? "";
                                return name ? (
                                  <option key={`${name}-${i}`} value={name}>{name}</option>
                                ) : null;
                              })}
                            </Select>
                            <span className="text-[10px] text-muted-foreground">
                              Fuérzalo solo para casos raros.
                            </span>
                          </div>
                          <div className="col-span-2 flex items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2 text-[11px] text-primary">
                            <MapPin size={12} className="flex-none" />
                            <span>
                              Al crear el contrato se aprovisiona{" "}
                              <strong className="font-mono">{serialLargo(onuSel.serial)}</strong> en{" "}
                              <strong>{onuSel.olt} · PON {onuSel.pon ?? "?"}</strong> y se
                              liga cliente/contrato en ByteOLT. Si la OLT rechaza, el alta
                              no se completa.
                            </span>
                          </div>
                        </div>
                      )}

                      {/* ═══ rc111 — Módem · ACS (TR-069) ═══
                          rc118 — oculto en MATCH: al ACS no se manda nada. */}
                      {byteacsEnabled && !matchMode && (
                        <div className="mt-3 rounded-md border bg-muted/20 p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <Label className="mb-0">
                              Módem · ACS (TR-069){" "}
                              <span className="font-mono text-[10px] font-normal text-primary">
                                · autoconfig vía ByteACS
                              </span>
                            </Label>
                            <div className="flex gap-1.5">
                              <Button
                                type="button" variant="outline" size="sm"
                                onClick={() => setShowCaptura(true)}
                              >
                                Escanear etiqueta
                              </Button>
                              <Button
                                type="button" variant="outline" size="sm"
                                onClick={() => setShowAlmacen((v) => !v)}
                              >
                                {showAlmacen ? "Ocultar almacén" : "Ver almacén"}
                              </Button>
                            </div>
                          </div>
                          <Label>Serial del módem</Label>
                          <Input
                            value={acsSerial}
                            onChange={(e) => {
                              acsSerialDirty.current = true;
                              setAcsSerial(e.target.value.toUpperCase());
                            }}
                            placeholder="Auto: la serie de la ONU elegida"
                            className="font-mono"
                          />
                          <p className="mt-1 text-[10px] text-muted-foreground">
                            Por default la serie de la ONU. Al crear el contrato se
                            registra en ByteACS y el módem se autoconfigura (WAN PPPoE +
                            WiFi) en su primer Inform.
                          </p>

                          {/* rc122 — SSID personalizado (conservar la red anterior) */}
                          <div className="mt-2">
                            <Label>SSID personalizado (opcional)</Label>
                            <Input
                              value={ssidCustom}
                              onChange={(e) => setSsidCustom(e.target.value)}
                              placeholder="Vacío = prefijo del ACS + últimos 6 de la MAC"
                              maxLength={32}
                            />
                            <p className="mt-1 text-[10px] text-muted-foreground">
                              Para conservar el nombre de la red anterior del cliente (sin
                              reconectar sus equipos). Requiere ByteACS 0.53+.
                            </p>
                          </div>

                          {/* rc121 — clave WiFi opcional (como en ByteACS) */}
                          <div className="mt-2">
                            <Label>Contraseña WiFi (opcional)</Label>
                            <div className="flex gap-1.5">
                              <Input
                                value={wifiPass}
                                onChange={(e) => setWifiPass(e.target.value)}
                                placeholder="Vacía = clave de fábrica (etiqueta)"
                                className="font-mono"
                              />
                              <Button
                                type="button" variant="outline" size="sm"
                                onClick={() => setWifiPass(genWifiPass())}
                              >
                                Generar
                              </Button>
                            </div>
                            <p className="mt-1 text-[10px] text-muted-foreground">
                              Si la asignas (8-63), ByteACS la aplica en el primer Inform
                              y queda lista para la <strong>tarjeta QR</strong> de la ficha
                              del contrato. Vacía = el módem conserva la de su etiqueta
                              (tarjeta sin QR).
                            </p>
                          </div>

                          {/* Estado en vivo del serial en ByteACS */}
                          {acsSerialQuery.length >= 8 && (
                            <div className="mt-2 text-[11px] leading-relaxed">
                              {equipoAcsLoading ? (
                                <span className="text-muted-foreground">
                                  Consultando en ByteACS…
                                </span>
                              ) : equipoAcs?.found === false ? (
                                <span className="text-primary">
                                  Serial nuevo en ByteACS — se registra al crear el
                                  contrato y se configura en su primer Inform.
                                  {equipoAcs.pending ? " (Tenía un registro previo pendiente: se reinicia.)" : ""}
                                </span>
                              ) : equipoAcs?.found === true && equipoAcs.link?.client_nombre ? (
                                <span className="font-medium text-amber-600 dark:text-amber-400">
                                  ⚠ Este módem ya está LIGADO en ByteACS a{" "}
                                  {equipoAcs.link.client_nombre}
                                  {equipoAcs.link.contract_codigo
                                    ? ` (${equipoAcs.link.contract_codigo}${equipoAcs.link.contract_estatus ? ` · ${equipoAcs.link.contract_estatus}` : ""})`
                                    : ""}{" "}
                                  — verifica antes de reusarlo.
                                </span>
                              ) : equipoAcs?.found === true && equipoAcs.lifecycle === "retired" ? (
                                <span className="text-emerald-600 dark:text-emerald-400">
                                  ✓ En ALMACÉN de ByteACS
                                  {equipoAcs.product_class ? ` (${equipoAcs.product_class})` : ""} —
                                  al crear el contrato se reactiva y reconfigura solo.
                                </span>
                              ) : equipoAcs?.found === true ? (
                                <span className="text-emerald-600 dark:text-emerald-400">
                                  ✓ Registrado en ByteACS y libre
                                  {equipoAcs.product_class ? ` (${equipoAcs.product_class})` : ""} —
                                  se re-registra y reconfigura al crear.
                                </span>
                              ) : equipoAcs ? (
                                <span className="text-muted-foreground">
                                  No se pudo consultar (ByteACS anterior a 0.49) — el
                                  registro al crear funciona igual.
                                </span>
                              ) : null}
                            </div>
                          )}

                          {/* Almacén: equipos retirados listos para reconfig */}
                          {showAlmacen && (
                            <div className="mt-2 overflow-hidden rounded-md border">
                              <div className="border-b bg-muted/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                                Almacén ByteACS · {almacenData?.equipos?.length ?? "…"}
                              </div>
                              {almacenLoading ? (
                                <p className="p-3 text-center text-[11px] text-muted-foreground">
                                  Consultando almacén…
                                </p>
                              ) : (almacenData?.equipos?.length ?? 0) === 0 ? (
                                <p className="p-3 text-center text-[11px] text-muted-foreground">
                                  No hay equipos en el almacén de ByteACS.
                                </p>
                              ) : (
                                almacenData!.equipos.map((eq) => (
                                  <button
                                    type="button"
                                    key={eq.serial}
                                    onClick={() => {
                                      acsSerialDirty.current = true;
                                      setAcsSerial((eq.serial ?? "").toUpperCase());
                                      setShowAlmacen(false);
                                    }}
                                    className={cn(
                                      "flex w-full items-center gap-3 border-b px-3 py-2 text-left text-xs last:border-b-0",
                                      acsSerial === (eq.serial ?? "").toUpperCase()
                                        ? "bg-primary/10"
                                        : "hover:bg-primary/5",
                                    )}
                                  >
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate font-mono text-[11.5px]">
                                        {eq.serial}
                                      </span>
                                      <span className="block truncate text-[10px] text-muted-foreground">
                                        {[eq.manufacturer, eq.product_class, eq.retired_reason]
                                          .filter(Boolean).join(" · ") || "—"}
                                      </span>
                                    </span>
                                    <span className="flex-none font-mono text-[9.5px] text-muted-foreground">
                                      {eq.retired_at ? eq.retired_at.slice(0, 10) : ""}
                                    </span>
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-3 grid grid-cols-2 gap-3">
                        {/* rc113 — MAC manual (además del escáner y el autofill):
                            va a fiber_detalle.onu_mac y de ahí al SSID de ByteACS */}
                        <div>
                          <Label>MAC del módem (ONU)</Label>
                          <UpperInput
                            control={control}
                            name="fiber_detalle.onu_mac"
                            placeholder="14:65:6A:95:AC:F6"
                          />
                          <span className="text-[10px] text-muted-foreground">
                            Se llena sola al escanear la etiqueta o si ByteOLT la conoce;
                            aquí la puedes teclear. SSID = prefijo del ACS + últimos 6.
                          </span>
                        </div>
                        <div>
                          <Label className="flex items-center justify-between gap-2">
                            <span>IP management</span>
                            {ipAsignada && !onuMgmtDirty.current && (
                              <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                                auto · {ipAsignada}
                              </span>
                            )}
                          </Label>
                          <Input
                            {...register("fiber_detalle.onu_ip_management", {
                              onChange: () => { onuMgmtDirty.current = true; },
                            })}
                            placeholder="Se auto-llena con IP asignada"
                          />
                        </div>
                        <div>
                          <Label>Sitio OLT</Label>
                          <Combobox
                            options={sitioFiberOptions}
                            value={(watch("fiber_detalle.sitio_olt_id") as string) ?? ""}
                            onChange={(v) => setValue("fiber_detalle.sitio_olt_id", v)}
                            placeholder="Buscar OLT…"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Notas de instalación</Label>
                          <Textarea rows={2} {...register("fiber_detalle.notas_instalacion")} />
                        </div>
                      </div>
                  {ubicacionSection}

                    </>
                  )}
                </TabsContent>
              )}
            </Tabs>
            {/* rc145 — resumen: columna en escritorio, plegable en móvil */}
            <aside className="max-md:hidden">{resumenPanel}</aside>
            <details className="mt-3 md:hidden">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Ver resumen del contrato</summary>
              <div className="mt-2">{resumenPanel}</div>
            </details>
            </div>

            {error && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </DialogContent>

          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "Creando…" : "Crear contrato"}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      {/* rc111 — escáner de etiqueta (serial + MAC) estilo ByteACS */}
      {showCaptura && (
        <LabelCaptureOnu
          onClose={() => setShowCaptura(false)}
          onUse={(d) => {
            if (d.serial) {
              // rc112 — en pre-registro el serial escaneado va al pre-registro
              // (y de ahí se propaga solo a PPPoE + módem ACS); si no, al ACS.
              // rc118 — en MATCH va al serial del match.
              if (matchMode) {
                setMatchSerial(d.serial);
              } else if (preMode) {
                setPreSerial(d.serial);
              } else {
                acsSerialDirty.current = true;
                setAcsSerial(d.serial);
              }
            }
            if (d.mac) {
              setValue("fiber_detalle.onu_mac", fmtMac(d.mac), { shouldDirty: true });
            }
          }}
        />
      )}

      {confirmData && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) setConfirmData(null); }} size="sm">
          <DialogHeader>
            <DialogTitle>Confirmar alta del contrato</DialogTitle>
            <DialogDescription>
              Revisa la empresa de cobro y la factura de instalación antes de escribir en sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{confirmCliente?.razon_social ?? "Cliente"}</p>
                  <p className="text-muted-foreground">{confirmCliente?.codigo}</p>
                </div>
                <EmpresaChip empresaId={confirmCliente?.empresa_id} alwaysShow size="md" />
              </div>
              <p className="mt-2 leading-relaxed text-muted-foreground">
                Los cobros de este contrato se enrutarán a la cuenta de esa empresa.
                ¿Empresa equivocada? Cancela, corrige la empresa en el editor del
                cliente y vuelve a crear el contrato.
              </p>
            </div>
            <div className="mt-3">
              <Label htmlFor="inst_monto">Factura de instalación (MXN)</Label>
              <Input
                id="inst_monto"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={instMonto}
                onChange={(e) => setInstMonto(e.target.value)}
              />
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                Se emite al crear el contrato (vence hoy) y sube a Toku de inmediato —
                así el pago del cliente se engancha aunque transfiera en este momento.
                Déjalo en 0 para no generar factura de instalación.
              </p>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmData(null)}>
              Cancelar
            </Button>
            <Button type="button" size="sm" onClick={confirmCreate} disabled={submitting}>
              {Number(instMonto) > 0 ? "Crear contrato + factura" : "Crear contrato sin factura"}
            </Button>
          </DialogFooter>
        </Dialog>
      )}

      {revealedPassword && (
        <PasswordRevealDialog
          open={true}
          onConfirm={handlePasswordConfirmed}
          pppoeUser={revealedPassword.pppoe_user}
          password={revealedPassword.pppoe_password_plain}
          title="Contrato creado — Credenciales PPPoE"
          subtitle={`Contrato ${revealedPassword.codigo} creado. Guarda estas credenciales antes de cerrar.`}
        />
      )}
    </>
  );
}
